import { Timestamp } from 'firebase/firestore';
import moment, { Moment } from 'moment-timezone';

import { COMPONENT_KEY_VALUE_BY_TYPE } from '@src/packages/utils/constants/eventTypes';
import repetitionFactory from '@src/packages/utils/functions/schedule/repetitionFactory';

import { ScheduleType } from '@src/models/interfaces/schedule.interface';
import type { ScheduleSaveInput } from '@src/models/interfaces/schedule.interface';
import TSchedule from '@src/models/interfaces/schedule.interface';
import TScheduleTrigger from '@src/models/interfaces/scheduleTrigger.interface';
import type {
  WorkOrderFactoryOutput,
  WorkOrderFactoryParams,
  WorkOrderFormsMap,
  WorkOrderLocationOption,
  WorkOrderMemberUpdateParams,
  WorkOrderTemplate,
  WorkOrderTemplateForm,
  WhenShouldOccurValue,
} from '@src/models/interfaces/workOrder.interface';
import { ScheduleMapper } from './schedule.mapper';

export const INPUT_TYPES_NOT_ALLOWED = ['video', 'file'];
export const WORK_FORM_REGEX = /work_form_(\w+)/;

export const WHEN_SHOULD_OCCUR_VALUES = {
  IMMEDIATELY: 'immediately',
  CUSTOM: 'custom',
} as const;

export class WorkOrderMapper {
  static workOrderFactory({
    template,
    data,
    whenShouldOccur,
  }: WorkOrderFactoryParams): WorkOrderFactoryOutput {
    const {
      divId,
      startDateOptional,
      startTimeOptional,
      id: templateId,
      name: templateName,
      triggersFollowIndex = false,
    } = template;
    const {
      workOrderName,
      forms,
      triggers,
      startDate,
      timezone,
      startTime,
      members,
      duration,
      isExpiredAllowed,
      expiredAllowedTime,
    } = data;
    const createdAt = moment().tz(timezone, true);
    const useCreatedAt =
      whenShouldOccur === WHEN_SHOULD_OCCUR_VALUES.IMMEDIATELY ||
      (whenShouldOccur === WHEN_SHOULD_OCCUR_VALUES.CUSTOM && !startTimeOptional);

    const startAtTz = (() => {
      if (useCreatedAt) return createdAt.clone();
      const base = startDateOptional ? moment(startDate).tz(timezone, true) : createdAt.clone();
      if (startTimeOptional) {
        base.hour(startTime.hour()).minute(startTime.minute());
      }
      base.seconds(0);
      return base;
    })();

    const endDate = startAtTz.clone().add(duration, 'minutes');
    const endAtTz = moment(endDate).tz(timezone, true);

    return {
      divId,
      duration,
      members,
      name: workOrderName || template?.name || '',
      schedule: {
        startAt: Timestamp.fromMillis(startAtTz.valueOf()),
        endAt: Timestamp.fromMillis(endAtTz.valueOf()),
        timeZone: timezone,
        repetition: {
          day: [startAtTz.date()],
          hour: [startAtTz.hours()],
          minute: [startAtTz.minutes()],
          month: [startAtTz.month() + 1],
          weekDay: [],
          year: [startAtTz.year()],
        },
      },
      reminders: [],
      triggers,
      forms,
      type: ScheduleType.Task,
      isExpiredAllowed,
      expiredAllowedTime,
      taskSummary: {
        id: templateId,
        name: templateName,
      },
      triggersFollowIndex,
    };
  }

  static fromFactoryOutput(output: WorkOrderFactoryOutput, scheduleId = ''): ScheduleSaveInput {
    const parsedTriggers = ScheduleMapper.applyTriggersFactory(
      output.triggers,
      output.triggersFollowIndex,
    ).map(trigger => ScheduleMapper.toSaveTrigger(trigger));

    return {
      id: scheduleId,
      divId: output.divId,
      duration: output.duration,
      members: output.members,
      name: output.name.trim(),
      reminders: output.reminders,
      schedule: {
        startAt: output.schedule.startAt as ScheduleSaveInput['schedule']['startAt'],
        endAt: output.schedule.endAt as ScheduleSaveInput['schedule']['endAt'],
        repetition: output.schedule.repetition,
        timeZone: output.schedule.timeZone,
      },
      triggers: parsedTriggers,
      forms: output.forms as ScheduleSaveInput['forms'],
      type: ScheduleType.Task,
      isExpiredAllowed: output.isExpiredAllowed,
      expiredAllowedTime: output.expiredAllowedTime,
      taskSummary: output.taskSummary,
      triggersFollowIndex: output.triggersFollowIndex,
    };
  }

  static toMemberUpdateInput(
    existingSchedule: TSchedule,
    {
      members,
      resetCounter,
      timeZone,
      duration,
    }: Omit<WorkOrderMemberUpdateParams, 'organizationId' | 'divisionId' | 'scheduleId'>,
  ): ScheduleSaveInput {
    let schedule = { ...existingSchedule.schedule };

    if (resetCounter) {
      const newStartAtTz = moment(new Date()).tz(timeZone, true).seconds(0);
      const newEndDate = newStartAtTz.clone().add(duration, 'minutes');
      const repetition = repetitionFactory(newStartAtTz);

      schedule = {
        ...schedule,
        startAt: Timestamp.fromMillis(newStartAtTz.valueOf()),
        endAt: Timestamp.fromMillis(newEndDate.valueOf()),
        repetition: repetition ?? schedule.repetition,
      };
    }

    const parsedTriggers = (existingSchedule.triggers ?? []).map(trigger =>
      ScheduleMapper.toSaveTrigger(trigger),
    );

    return {
      id: existingSchedule.id,
      divId: existingSchedule.divId,
      duration: existingSchedule.duration,
      members,
      name: existingSchedule.name,
      reminders: existingSchedule.reminders ?? [],
      schedule,
      triggers: parsedTriggers,
      forms: existingSchedule.forms ?? [],
      type: ScheduleType.Task,
      isExpiredAllowed: existingSchedule.isExpiredAllowed,
      expiredAllowedTime: existingSchedule.expiredAllowedTime,
      taskSummary: existingSchedule.taskSummary,
      triggersFollowIndex: existingSchedule.triggersFollowIndex ?? false,
    };
  }

  static async getValuesFromDynamicForm(
    forms: WorkOrderFormsMap,
    validate = true,
  ): Promise<Record<string, Record<string, unknown>>> {
    const allFormValues: Record<string, Record<string, unknown>> = {};

    for (const key of Object.keys(forms)) {
      if (key.match(WORK_FORM_REGEX)) {
        if (validate) {
          await forms[key].validateFields();
        }
        allFormValues[key] = forms[key].getFieldsValue();
      }
    }

    return allFormValues;
  }

  static formValuesFactory(
    formValues: Record<string, Record<string, unknown>> | null,
    forms: WorkOrderTemplateForm[] | undefined,
  ): WorkOrderTemplateForm[] | null {
    if (!formValues || !forms) return null;

    return forms.map(form => {
      const formKey = form.id.replace('work_form_', '');

      const updatedComponents = form.components
        .filter(item => !INPUT_TYPES_NOT_ALLOWED.includes(item.type))
        .map(component => {
          const componentValue = (
            formValues[`work_form_${formKey}`]?.[component.id] as
              Record<string, unknown> | undefined
          )?.[component?.label?.name ?? ''];

          let val: unknown = componentValue;
          if (component.type === 'select') {
            val = [componentValue];
          } else if (component.type === 'listSelect') {
            // The input uses labelInValue, so the answer keeps both the item id and its label.
            const selected = componentValue as { value: string; label: string } | undefined;
            return {
              ...component,
              selectValue: selected ? [selected.label] : [],
              selectOptionIds: selected ? [selected.value] : [],
            };
          } else if (component.type === 'date' && componentValue) {
            val = Timestamp.fromMillis((componentValue as Moment).valueOf());
          } else if (component.type === 'signature') {
            const signature = componentValue as
              | { url?: string; clarificationValue?: string }
              | undefined;

            return {
              ...component,
              signatureValue: signature?.url ? { url: signature.url } : undefined,
              clarificationValue: component.componentProps?.includeClarification
                ? signature?.clarificationValue
                : undefined,
            };
          }

          return {
            ...component,
            [COMPONENT_KEY_VALUE_BY_TYPE[
              component.type as keyof typeof COMPONENT_KEY_VALUE_BY_TYPE
            ]]: val,
          };
        });

      return {
        ...form,
        components: updatedComponents,
      };
    });
  }

  static async validateTaskForms(forms: WorkOrderFormsMap): Promise<boolean> {
    for (const key of Object.keys(forms)) {
      if (key.includes('task')) {
        await forms[key].validateFields();
      }
    }
    return true;
  }

  static getValuesFromTaskForms(
    forms: WorkOrderFormsMap,
    tasks: TScheduleTrigger[],
    locations: WorkOrderLocationOption[],
  ): TScheduleTrigger[] {
    const formValues: Record<string, Record<string, unknown>> = {};

    for (const key of Object.keys(forms)) {
      if (key.includes('task')) {
        const taskForm = forms[key];
        const taskFormValues = taskForm.getFieldsValue();
        const taskId = key.replace('task_', '');
        formValues[taskId] = taskFormValues;
      }
    }

    return tasks.map(task => {
      const newTask = { ...task };
      const locationIdSelected = formValues[task.id]?.[task.id];
      const location = locations.find(loc => loc.id === locationIdSelected);
      newTask.locations = location ? [location as TScheduleTrigger['locations'][number]] : [];
      delete newTask.locationOptional;
      delete newTask.locationRequired;
      newTask.locationGroup = null;

      return newTask;
    });
  }

  static generateNameByForms(components: WorkOrderTemplateForm['components'] = []): string {
    const componentsLimited = components.slice(0, 3);
    const componentValues: string[] = [];

    const concatenableComponents = componentsLimited.filter(
      component =>
        component.type === 'text' ||
        component.type === 'select' ||
        component.type === 'listSelect' ||
        component.type === 'tag' ||
        component.type === 'number',
    );

    for (let i = 0; i < Math.min(concatenableComponents.length, 3); i++) {
      const currentComponent = concatenableComponents[i];
      const value =
        currentComponent[
          COMPONENT_KEY_VALUE_BY_TYPE[
            currentComponent.type as keyof typeof COMPONENT_KEY_VALUE_BY_TYPE
          ] as keyof typeof currentComponent
        ];

      if (componentValues.length <= 3) {
        if (typeof value === 'object' && value !== null && Array.isArray(value)) {
          componentValues.push(value.map(v => String(v)).join('-'));
        } else if (value !== undefined && value !== null) {
          componentValues.push(String(value));
        }
      }
    }

    const output = componentValues.filter(value => value !== null && value !== undefined);
    const endLabel = output.join('-').replace('--', '-');
    const MAX_END_LABEL_LENGTH = 180;

    return endLabel.slice(0, MAX_END_LABEL_LENGTH);
  }

  static isWhenShouldOccurDisabledByTemplate(
    value: WhenShouldOccurValue,
    template: WorkOrderTemplate | null,
  ): boolean {
    return (
      value === WHEN_SHOULD_OCCUR_VALUES.CUSTOM &&
      !template?.startDateOptional &&
      !template?.startTimeOptional
    );
  }
}

export const workOrderFactory = WorkOrderMapper.workOrderFactory.bind(WorkOrderMapper);
export const getValuesFromDynamicForm =
  WorkOrderMapper.getValuesFromDynamicForm.bind(WorkOrderMapper);
export const formValuesFactory = WorkOrderMapper.formValuesFactory.bind(WorkOrderMapper);
export const validateTaskForms = WorkOrderMapper.validateTaskForms.bind(WorkOrderMapper);
export const getValuesFromTaskForms = WorkOrderMapper.getValuesFromTaskForms.bind(WorkOrderMapper);
export const generateNameByForms = WorkOrderMapper.generateNameByForms.bind(WorkOrderMapper);
export const isWhenShouldOccurDisabledByTemplate =
  WorkOrderMapper.isWhenShouldOccurDisabledByTemplate.bind(WorkOrderMapper);
